import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { apiPathV4 } from '../config';
import { getAuthToken } from '../utils/authUtils';
import {
  adjustActualUnreadCount,
  filterVisibleNotifications,
  hideNotificationLocally,
  isAnnouncementType,
  isNotificationRead,
  isUserScopedNotification,
  peekLastBackendUnreadCount,
  readHiddenNotificationIds,
  reconcileActualUnreadCount,
  setActualUnreadCount,
  writeHiddenNotificationIds,
  type NotificationLike,
} from '../utils/notificationUtils';
import { scheduleDeferred } from '../utils/scheduleDeferred';
import { useAppAuth } from './AppContext';
import { useRequiredWebSocket } from '../hooks/useRequiredWebSocket';

export type Notification = NotificationLike;

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  loaded: boolean;
}

type NotificationsContextValue = {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  loaded: boolean;
  fetchNotifications: (page?: number, append?: boolean) => Promise<void>;
  fetchMore: () => void;
  markAsRead: (notification: Notification) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  refreshUnreadCount: () => Promise<void>;
};

const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 2 * 60_000;
const LOCAL_STATE_SYNC_EVENT = 'notifications:refresh-unread-count';
const LOCAL_STATE_SOURCE = 'notifications-context';

type LocalStateSyncEventDetail = {
  source?: string;
  notificationId?: number;
  unreadCount?: number;
  hiddenIds?: number[];
  reset?: boolean;
};

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const buildClearedState = (prev: NotificationsState): NotificationsState => ({
  ...prev,
  notifications: [],
  unreadCount: 0,
  hasMore: false,
  page: 1,
  loaded: false,
});

const removeNotificationFromState = (
  prev: NotificationsState,
  notificationId: number,
  nextUnreadCount: number
): NotificationsState => {
  const nextNotifications = prev.notifications.filter(
    notification => notification.id !== notificationId
  );

  if (nextNotifications.length === 0) {
    return {
      ...buildClearedState(prev),
      unreadCount: nextUnreadCount,
    };
  }

  return {
    ...prev,
    notifications: nextNotifications,
    unreadCount: nextUnreadCount,
  };
};

const filterBellNotifications = (notifications: Notification[]): Notification[] =>
  notifications.filter(notification => !isAnnouncementType(notification.type));

const isClearedNotificationsState = (state: NotificationsState): boolean =>
  state.notifications.length === 0 &&
  state.unreadCount === 0 &&
  !state.isLoading &&
  !state.hasMore &&
  state.page === 1 &&
  !state.loaded;

const sameNumberSets = (left: Set<number>, right: Set<number>): boolean =>
  left.size === right.size && [...right].every(id => left.has(id));

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}

export function NotificationsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { getIdTokenClaims, getAccessTokenSilently, user } = useAppAuth();
  const { sendMessage, addListener, removeListener, webSocketStatus } = useRequiredWebSocket();

  const [state, setState] = useState<NotificationsState>({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    hasMore: false,
    page: 1,
    loaded: false,
  });
  const [sessionHiddenIds, setSessionHiddenIds] = useState<Set<number>>(() =>
    readHiddenNotificationIds()
  );

  const subscribedRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      let token = await getAuthToken(getIdTokenClaims);
      if (!token) {
        token = await getAccessTokenSilently();
      }
      return token;
    } catch {
      return null;
    }
  }, [getIdTokenClaims, getAccessTokenSilently]);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) return;

      const response = await fetch(`${apiPathV4()}/notification/unread-count`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return;

      const data = await response.json();
      const backendUnreadCount = data.unread_count ?? 0;
      const previousLastBackend = peekLastBackendUnreadCount();
      const reconciledCount = reconcileActualUnreadCount(
        backendUnreadCount,
        sessionHiddenIds
      );
      const backendDelta =
        previousLastBackend === null ? null : backendUnreadCount - previousLastBackend;

      setState(prev => {
        // Authoritative complete bell list: keep badge = visible items while the
        // API total is unchanged. If the API moved (new/expired rows, including
        // announcements), invalidate so the next open refetches and avoid applying
        // raw API deltas onto a bell-visible actual count.
        if (prev.loaded && !prev.hasMore) {
          if (backendDelta === 0) {
            const authoritative = prev.notifications.length;
            if (reconciledCount !== authoritative) {
              setActualUnreadCount(authoritative);
            }
            return {
              ...prev,
              unreadCount: authoritative,
            };
          }

          return {
            ...prev,
            unreadCount: reconciledCount,
            notifications: [],
            hasMore: false,
            page: 1,
            loaded: false,
          };
        }

        return {
          ...prev,
          unreadCount: reconciledCount,
          notifications: reconciledCount > 0 ? prev.notifications : [],
          hasMore: reconciledCount > 0 ? prev.hasMore : false,
          page: reconciledCount > 0 ? prev.page : 1,
          loaded: reconciledCount > 0 ? prev.loaded : false,
        };
      });
    } catch {
      // silently ignore polling errors
    }
  }, [getToken, sessionHiddenIds]);

  const fetchNotifications = useCallback(
    async (page = 1, append = false) => {
      setState(prev => ({ ...prev, isLoading: true }));
      try {
        const token = await getToken();
        if (!token) return;

        const params = new URLSearchParams({
          page: String(page),
          limit: String(PAGE_SIZE),
          unread_only: 'true',
        });

        const response = await fetch(`${apiPathV4()}/notification?${params}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }

        const data = await response.json();
        const items: Notification[] = filterBellNotifications(
          filterVisibleNotifications(data.items || [], sessionHiddenIds)
        );
        const hasMore: boolean = data.has_more ?? false;
        // Keep last_backend in sync with the API, then override actual count with
        // the authoritative bell-visible list when we have the full unread page.
        const reconciledCount = reconcileActualUnreadCount(
          data.unread_count ?? 0,
          sessionHiddenIds
        );
        const unreadCount =
          !append && page === 1 && !hasMore
            ? setActualUnreadCount(items.length)
            : reconciledCount;

        setState(prev => ({
          ...prev,
          notifications: append
            ? [
                ...prev.notifications,
                ...items.filter(
                  item => !prev.notifications.some(existing => existing.id === item.id)
                ),
              ]
            : items,
          unreadCount,
          hasMore,
          page,
          isLoading: false,
          loaded: true,
        }));
      } catch {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    },
    [getToken, sessionHiddenIds]
  );

  const markAsRead = useCallback(
    async (notification: Notification): Promise<boolean> => {
      try {
        if (isNotificationRead(notification, sessionHiddenIds)) {
          return true;
        }

        let nextHiddenIds = sessionHiddenIds;
        let nextUnreadCount = Math.max(0, state.unreadCount - 1);

        if (isUserScopedNotification(notification)) {
          const token = await getToken();
          if (!token) return false;

          const response = await fetch(`${apiPathV4()}/notification/${notification.id}/read`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            return false;
          }
          nextUnreadCount = adjustActualUnreadCount({
            actualDelta: -1,
            backendDelta: -1,
          });
        } else {
          nextHiddenIds = hideNotificationLocally(notification.id);
          setSessionHiddenIds(nextHiddenIds);
          nextUnreadCount = adjustActualUnreadCount({
            actualDelta: -1,
          });
        }

        setState(prev => removeNotificationFromState(prev, notification.id, nextUnreadCount));
        window.dispatchEvent(
          new CustomEvent<LocalStateSyncEventDetail>(LOCAL_STATE_SYNC_EVENT, {
            detail: {
              source: LOCAL_STATE_SOURCE,
              notificationId: notification.id,
              unreadCount: nextUnreadCount,
              hiddenIds: Array.from(nextHiddenIds),
            },
          })
        );
        return true;
      } catch {
        // silently fail
        return false;
      }
    },
    [getToken, sessionHiddenIds]
  );

  const markAllAsRead = useCallback(async (): Promise<boolean> => {
    try {
      const userScopedNotifications = state.notifications.filter(isUserScopedNotification);
      const sessionScopedNotifications = state.notifications.filter(
        notification => !isUserScopedNotification(notification)
      );

      let readAllSucceeded = true;
      if (userScopedNotifications.length > 0) {
        const token = await getToken();
        if (!token) return false;

        const response = await fetch(`${apiPathV4()}/notification/read-all`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        readAllSucceeded = response.ok;
      }

      let nextHiddenIds = sessionHiddenIds;
      if (sessionScopedNotifications.length > 0) {
        nextHiddenIds = new Set(sessionHiddenIds);
        sessionScopedNotifications.forEach(notification => nextHiddenIds.add(notification.id));
        writeHiddenNotificationIds(nextHiddenIds);
        setSessionHiddenIds(nextHiddenIds);
      }

      const nextUnreadCount = readAllSucceeded
        ? setActualUnreadCount(0)
        : setActualUnreadCount(userScopedNotifications.length);

      setState(prev => {
        if (readAllSucceeded) {
          return {
            ...buildClearedState(prev),
            unreadCount: nextUnreadCount,
          };
        }

        const visibleNotifications = prev.notifications.filter(isUserScopedNotification);
        return {
          ...prev,
          notifications: visibleNotifications,
          unreadCount: nextUnreadCount,
          hasMore: false,
          page: 1,
          loaded: visibleNotifications.length > 0,
        };
      });

      window.dispatchEvent(
        new CustomEvent<LocalStateSyncEventDetail>(LOCAL_STATE_SYNC_EVENT, {
          detail: {
            source: LOCAL_STATE_SOURCE,
            unreadCount: nextUnreadCount,
            hiddenIds: Array.from(nextHiddenIds),
            reset: readAllSucceeded,
          },
        })
      );
      return readAllSucceeded;
    } catch {
      // silently fail
      return false;
    }
  }, [getToken, sessionHiddenIds, state.notifications]);

  const fetchMore = useCallback(() => {
    if (!state.isLoading && state.hasMore) {
      void fetchNotifications(state.page + 1, true);
    }
  }, [fetchNotifications, state.isLoading, state.hasMore, state.page]);

  useEffect(() => {
    if (!user?.email || subscribedRef.current) return;

    const wsStatus = webSocketStatus?.current?.current ?? webSocketStatus?.current;
    if (wsStatus !== 'open') return;

    sendMessage(
      JSON.stringify({
        action: 'subscribe',
        subscription_group: 'notifications',
        resource_id: user.email.toLowerCase(),
      })
    );
    subscribedRef.current = true;
  }, [user, sendMessage, webSocketStatus]);

  useEffect(() => {
    const handleMessage = (message: string) => {
      try {
        const data = JSON.parse(message);
        if (data?.type === 'notification' && data?.notification) {
          const newNotification: Notification = data.notification;
          if (isNotificationRead(newNotification, sessionHiddenIds)) {
            return;
          }

          const nextUnreadCount = adjustActualUnreadCount({
            actualDelta: 1,
            backendDelta: 1,
          });

          setState(prev => ({
            ...prev,
            notifications: prev.loaded
              ? [
                  ...filterBellNotifications([newNotification]),
                  ...prev.notifications.filter(item => item.id !== newNotification.id),
                ]
              : prev.notifications,
            unreadCount: nextUnreadCount,
          }));
        }
      } catch {
        // not a notification message
      }
    };

    addListener(handleMessage);
    return () => {
      removeListener(handleMessage);
    };
  }, [addListener, removeListener, sessionHiddenIds]);

  // Logout/reset must not depend on refreshUnreadCount: that callback is recreated
  // whenever sessionHiddenIds gets a new Set identity, which previously looped
  // setState → new Set → new callback → effect → setState ("Maximum update depth").
  useEffect(() => {
    if (user?.sub) {
      return;
    }

    setState(prev =>
      isClearedNotificationsState(prev)
        ? prev
        : {
            notifications: [],
            unreadCount: 0,
            isLoading: false,
            hasMore: false,
            page: 1,
            loaded: false,
          }
    );
    setSessionHiddenIds(prev => {
      const next = readHiddenNotificationIds();
      return sameNumberSets(prev, next) ? prev : next;
    });
    subscribedRef.current = false;
  }, [user?.sub]);

  useEffect(() => {
    if (!user?.sub) {
      return;
    }
    // Defer badge fetch so it does not compete with landing-page APIs.
    return scheduleDeferred(() => {
      void refreshUnreadCount();
    });
  }, [user?.sub, refreshUnreadCount]);

  useEffect(() => {
    if (!user?.sub) return;

    pollTimerRef.current = setInterval(() => {
      void refreshUnreadCount();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [user?.sub, refreshUnreadCount]);

  useEffect(() => {
    const handleLocalStateSync = (event: Event) => {
      const detail = (event as CustomEvent<LocalStateSyncEventDetail>).detail;
      if (detail?.source === LOCAL_STATE_SOURCE) {
        return;
      }

      const nextHiddenIds = Array.isArray(detail?.hiddenIds)
        ? new Set(detail.hiddenIds)
        : readHiddenNotificationIds();
      setSessionHiddenIds(nextHiddenIds);

      setState(prev => {
        const nextUnreadCount =
          typeof detail?.unreadCount === 'number'
            ? Math.max(0, Math.trunc(detail.unreadCount))
            : prev.unreadCount;

        if (detail?.reset) {
          return {
            ...buildClearedState(prev),
            unreadCount: nextUnreadCount,
          };
        }

        if (typeof detail?.notificationId === 'number') {
          return removeNotificationFromState(prev, detail.notificationId, nextUnreadCount);
        }

        return {
          ...prev,
          unreadCount: nextUnreadCount,
        };
      });
    };

    window.addEventListener(LOCAL_STATE_SYNC_EVENT, handleLocalStateSync);
    return () => {
      window.removeEventListener(LOCAL_STATE_SYNC_EVENT, handleLocalStateSync);
    };
  }, []);

  const value = useMemo(
    () => ({
      notifications: state.notifications,
      unreadCount: state.unreadCount,
      isLoading: state.isLoading,
      hasMore: state.hasMore,
      loaded: state.loaded,
      fetchNotifications,
      fetchMore,
      markAsRead,
      markAllAsRead,
      refreshUnreadCount,
    }),
    [
      state.notifications,
      state.unreadCount,
      state.isLoading,
      state.hasMore,
      state.loaded,
      fetchNotifications,
      fetchMore,
      markAsRead,
      markAllAsRead,
      refreshUnreadCount,
    ]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
